import { useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { DepthChartRandomizer } from '../services/depthChartRandomizer';
import { Player, Position, DepthChartEntry, Sport, Team } from '../types';

interface UseDepthChartRandomizerProps {
  players: Player[];
  positions: Position[];
  currentDepthChart: DepthChartEntry[];
  activeTab: string;
  sport: Sport;
  teamId: string;
  team?: Team;
  selectedFormation?: string; // Add selected formation context
  onRandomizationComplete: (entries: DepthChartEntry[]) => void;
}

interface RandomizationSettings {
  preserveColors: boolean;
  ensureFairPlayTime: boolean;
  showPreview: boolean;
  protectedPositions: { [inning: number]: { [position: string]: string } };
  standardProtectedPositions: { [position: string]: string }; // For non-baseball sports
  absentPlayerIds: string[];
}

export const useDepthChartRandomizer = ({
  players,
  positions,
  currentDepthChart,
  activeTab,
  sport,
  teamId,
  team,
  selectedFormation,
  onRandomizationComplete
}: UseDepthChartRandomizerProps) => {
  const [isRandomizing, setIsRandomizing] = useState(false);
  const [showRandomizeModal, setShowRandomizeModal] = useState(false);
  const [randomizationSettings, setRandomizationSettings] = useState<RandomizationSettings>({
    preserveColors: true, // Always true, option removed from UI
    ensureFairPlayTime: true,
    showPreview: true, // Always true, option removed from UI
    protectedPositions: {},
    standardProtectedPositions: {},
    absentPlayerIds: []
  });
  const [lastRandomizationStats, setLastRandomizationStats] = useState<any>(null);

  /**
   * Validate if randomization is possible
   */
  const canRandomize = useCallback(() => {
    const validation = DepthChartRandomizer.validateRandomizationParams(
      players,
      positions,
      {
        activeTab,
        sport,
        teamId,
        preserveColors: randomizationSettings.preserveColors,
        ensureFairPlayTime: randomizationSettings.ensureFairPlayTime,
        selectedFormation
      }
    );

    return validation.isValid;
  }, [players, positions, activeTab, sport, teamId, randomizationSettings, selectedFormation]);

  /**
   * Get preview information for randomization
   */
  const getRandomizationPreview = useCallback(() => {
    if (!canRandomize()) return null;

    const teamInnings = team?.innings;
    return DepthChartRandomizer.getRandomizationPreview(
      players,
      positions,
      {
        activeTab,
        sport,
        teamId,
        teamInnings,
        absentPlayerIds: randomizationSettings.absentPlayerIds,
        selectedFormation
      }
    );
  }, [players, positions, activeTab, sport, teamId, team?.innings, randomizationSettings.absentPlayerIds, canRandomize, selectedFormation]);

  /**
   * Execute randomization
   */
  const executeRandomization = useCallback(async () => {
    if (!canRandomize()) {
      const validation = DepthChartRandomizer.validateRandomizationParams(
        players,
        positions,
        { activeTab, sport, teamId, selectedFormation }
      );
      toast.error(validation.errors[0] || 'Cannot randomize depth chart');
      return;
    }

    setIsRandomizing(true);
    try {
      let result;
      const teamInnings = team?.innings;

      // Special handling for baseball/softball lineup
      if ((sport === 'Baseball' || sport === 'Softball') && activeTab === 'Lineup') {
        result = DepthChartRandomizer.randomizeLineupOrder(
          players,
          positions,
          currentDepthChart,
          {
            activeTab,
            sport,
            teamId,
            preserveColors: randomizationSettings.preserveColors,
            ensureFairPlayTime: randomizationSettings.ensureFairPlayTime,
            absentPlayerIds: randomizationSettings.absentPlayerIds,
            selectedFormation
          }
        );
      } else if ((sport === 'Baseball' || sport === 'Softball') && activeTab === 'Fielding') {
        // Special handling for baseball/softball fielding with protected positions
        result = DepthChartRandomizer.randomizeDepthChart(
          players,
          positions,
          currentDepthChart,
          {
            activeTab,
            sport,
            teamId,
            preserveColors: randomizationSettings.preserveColors,
            ensureFairPlayTime: randomizationSettings.ensureFairPlayTime,
            protectedPositions: randomizationSettings.protectedPositions,
            teamInnings,
            absentPlayerIds: randomizationSettings.absentPlayerIds,
            selectedFormation
          }
        );
      } else {
        // Standard randomization for all other cases with protected positions
        result = DepthChartRandomizer.randomizeDepthChart(
          players,
          positions,
          currentDepthChart,
          {
            activeTab,
            sport,
            teamId,
            preserveColors: randomizationSettings.preserveColors,
            ensureFairPlayTime: randomizationSettings.ensureFairPlayTime,
            standardProtectedPositions: randomizationSettings.standardProtectedPositions,
            absentPlayerIds: randomizationSettings.absentPlayerIds,
            selectedFormation
          }
        );
      }

      // Update the depth chart with randomized entries
      onRandomizationComplete(result.entries);
      
      // Store stats for display
      setLastRandomizationStats(result.statistics);

      // Show success message with stats
      const { playersAssigned, benchedPlayers, fairPlayTimeScore } = result.statistics;
      const absentCount = randomizationSettings.absentPlayerIds.length;
      
      let message = `Randomized ${playersAssigned} players`;
      
      if (absentCount > 0) {
        message += ` (${absentCount} absent)`;
      }
      
      if (benchedPlayers > 0) {
        message += `, ${benchedPlayers} on bench each inning`;
      }
      
      if (fairPlayTimeScore !== undefined) {
        message += `. Fair play score: ${fairPlayTimeScore}%`;
      }

      toast.success(message, { duration: 3000 });
      setShowRandomizeModal(false);

    } catch (error) {
      console.error('Randomization error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to randomize depth chart');
    } finally {
      setIsRandomizing(false);
    }
  }, [
    canRandomize,
    players,
    positions,
    currentDepthChart,
    activeTab,
    sport,
    teamId,
    team?.innings,
    randomizationSettings,
    onRandomizationComplete,
    selectedFormation
  ]);

  /**
   * Quick randomize without modal
   */
  const quickRandomize = useCallback(async () => {
    await executeRandomization();
  }, [executeRandomization]);

  /**
   * Show randomization modal
   */
  const showRandomizationModal = useCallback(() => {
    if (!canRandomize()) {
      const validation = DepthChartRandomizer.validateRandomizationParams(
        players,
        positions,
        { activeTab, sport, teamId, selectedFormation }
      );
      toast.error(validation.errors[0] || 'Cannot randomize depth chart');
      return;
    }
    setShowRandomizeModal(true);
  }, [canRandomize, players, positions, activeTab, sport, teamId, selectedFormation]);

  /**
   * Update randomization settings
   */
  const updateRandomizationSettings = useCallback((settings: Partial<RandomizationSettings>) => {
    setRandomizationSettings(prev => ({ ...prev, ...settings }));
  }, []);

  /**
   * Get randomization button text based on context
   */
  const getRandomizeButtonText = useCallback(() => {
    if ((sport === 'Baseball' || sport === 'Softball') && activeTab === 'Fielding') {
      return 'Randomize Innings';
    }
    if ((sport === 'Baseball' || sport === 'Softball') && activeTab === 'Lineup') {
      return 'Randomize Batting Order';
    }
    return 'Randomize Positions';
  }, [sport, activeTab]);

  /**
   * Get randomization description based on context
   */
  const getRandomizeDescription = useCallback(() => {
    if ((sport === 'Baseball' || sport === 'Softball') && activeTab === 'Fielding') {
      return 'Randomly assign players to fielding positions across all innings with bench rules: no back-to-back bench time, max 2 innings on bench';
    }
    if ((sport === 'Baseball' || sport === 'Softball') && activeTab === 'Lineup') {
      return 'Randomly shuffle the batting order';
    }
    return 'Randomly assign players to all positions and depth levels';
  }, [sport, activeTab]);

  /**
   * Get the team innings setting or default
   */
  const getTeamInnings = useCallback(() => {
    return team?.innings || 7; // Default to 7 innings
  }, [team?.innings]);

  return {
    // State
    isRandomizing,
    showRandomizeModal,
    randomizationSettings,
    lastRandomizationStats,
    
    // Computed
    canRandomize: canRandomize(),
    randomizationPreview: getRandomizationPreview(),
    randomizeButtonText: getRandomizeButtonText(),
    randomizeDescription: getRandomizeDescription(),
    teamInnings: getTeamInnings(),
    
    // Actions
    executeRandomization,
    quickRandomize,
    showRandomizationModal,
    updateRandomizationSettings,
    setShowRandomizeModal
  };
}; 